import { useEffect, useMemo, useState } from 'react';
import { Search } from 'lucide-react';
import FormModal, { Field, inputClass } from '../fieldWork/FormModal';
import inductionService from '../../services/induction';
import { errorMessage } from '../../utils/apiResponse';
import type { InductionPerson, PersonRef } from '../../types/induction';
import { WORKER_TYPES, personLabel } from './inductionLabels';

interface Props {
  induction: { id: number; title: string };
  /** People who already hold a current sign-off; not offered. */
  exclude: Set<PersonRef>;
  onClose: () => void;
  onDone: (count: number) => void;
}

type Basis = 'assessed' | 'prior_competency';

/** Sign several people off on one induction. */
export default function SignOffDialog({ induction, exclude, onClose, onDone }: Props) {
  const [people, setPeople] = useState<InductionPerson[] | null>(null);
  const [search, setSearch] = useState('');
  const [picked, setPicked] = useState<Set<PersonRef>>(new Set());
  const [basis, setBasis] = useState<Basis>('assessed');
  const [expiresOn, setExpiresOn] = useState('');
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    inductionService.people()
      .then(setPeople)
      .catch(e => { setPeople([]); setError(errorMessage(e, 'Could not load people')); });
  }, []);

  const offered = useMemo(() => (people ?? []).filter(p => !exclude.has(p.ref)), [people, exclude]);
  const shown = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return offered;
    return offered.filter(p => [p.name, p.picker_code, p.labour_provider_name].some(v => (v || '').toLowerCase().includes(q)));
  }, [offered, search]);

  const toggle = (ref: PersonRef) => setPicked(prev => {
    const next = new Set(prev);
    if (next.has(ref)) next.delete(ref); else next.add(ref);
    return next;
  });

  const submit = async () => {
    setSaving(true);
    setError(null);
    try {
      const refs = [...picked];
      const note = notes.trim() || undefined;
      if (expiresOn) {
        for (const ref of refs) {
          await inductionService.signOff({ ref, induction_ids: [induction.id], basis, notes: note, expires_on: expiresOn });
        }
      } else {
        await inductionService.signOffBulk(refs.map(ref => ({ ref, induction_ids: [induction.id] })), note, basis);
      }
      onDone(refs.length);
    } catch (e) {
      setError(errorMessage(e, 'Could not record the sign-offs'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <FormModal
      title="Sign people off"
      onClose={onClose}
      onSubmit={submit}
      submitLabel={picked.size ? `Sign off ${picked.size}` : 'Sign off'}
      submitting={saving}
      submitDisabled={picked.size === 0}
      error={error}
    >
      <p className="text-sm text-neutral-600 break-words">On <strong>{induction.title}</strong>.</p>

      <div>
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-neutral-400" />
          <input
            type="search"
            className={`${inputClass} pl-9`}
            placeholder="Search people"
            aria-label="Search people"
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
        </div>
        <div className="mt-2 max-h-56 overflow-y-auto rounded-lg border border-neutral-200 divide-y divide-neutral-100">
          {people === null && <p className="p-3 text-sm text-neutral-500">Loading…</p>}
          {people !== null && shown.length === 0 && (
            <p className="p-3 text-sm text-neutral-500">{offered.length ? 'Nobody matches.' : 'Everyone is already signed off.'}</p>
          )}
          {shown.map(p => (
            <label key={p.ref} className="flex items-start gap-2 px-3 py-2 text-sm cursor-pointer">
              <input type="checkbox" className="mt-0.5 shrink-0" checked={picked.has(p.ref)} onChange={() => toggle(p.ref)} />
              <span className="min-w-0 break-words">
                <span className="font-medium text-neutral-900">{personLabel(p.name)}</span>
                <span className="block text-xs text-neutral-500">
                  {[WORKER_TYPES[p.worker_type] ?? p.worker_type, p.labour_provider_name, p.picker_code].filter(Boolean).join(' · ')}
                </span>
              </span>
            </label>
          ))}
        </div>
        {picked.size > 0 && <p className="mt-1 text-xs text-neutral-500">{picked.size} selected</p>}
      </div>

      <fieldset className="space-y-2">
        <legend className="text-sm font-medium text-neutral-700 mb-1">Basis</legend>
        <label className="flex items-start gap-2 text-sm">
          <input type="radio" name="signoff-basis" className="mt-1 shrink-0" checked={basis === 'assessed'} onChange={() => setBasis('assessed')} />
          <span className="min-w-0 break-words"><span className="font-medium">Assessed</span> — you went through the induction and watched them operate it</span>
        </label>
        <label className="flex items-start gap-2 text-sm">
          <input type="radio" name="signoff-basis" className="mt-1 shrink-0" checked={basis === 'prior_competency'} onChange={() => setBasis('prior_competency')} />
          <span className="min-w-0 break-words"><span className="font-medium">Existing operator — recognised</span> — they already run it safely</span>
        </label>
      </fieldset>

      <Field label="Expires on (optional)" htmlFor="signoff-expires" hint="Leave blank to use the induction's refresher period, if it has one.">
        <input id="signoff-expires" type="date" className={inputClass} value={expiresOn} onChange={e => setExpiresOn(e.target.value)} />
      </Field>
      <Field label="Notes" htmlFor="signoff-notes">
        <textarea id="signoff-notes" className={inputClass} rows={2} value={notes} onChange={e => setNotes(e.target.value)} />
      </Field>
    </FormModal>
  );
}
